import {
  ChatAlt2Icon,
  PaperAirplaneIcon,
  XIcon
} from "@heroicons/react/solid";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useCommunication } from "../hooks/useCommunication";
import { communicationAPI } from "../services/communicationAPI";
import NewChatModal from "./TeamMessaging/NewChatModal";

const TeamMessaging = () => {
  const { user } = useAuth();
  const {
    currentConversation,
    messages,
    isConnected,
    typingUsers,
    messagesEndRef,
    sendMessage,
    selectConversation,
    setTyping,
    clearError,
  } = useCommunication();
  
  // Replicate CommunicationCenter state exactly
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [showNewChat, setShowNewChat] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [showChatOnMobile, setShowChatOnMobile] = useState(false);

  const messageInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Get current user info from multiple sources
  const getCurrentUser = () => {
    // Try from context first
    if (user && (user._id || user.id)) {
      return user;
    }

    // Try from localStorage as fallback
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        return JSON.parse(storedUser);
      }
    } catch (e) {
      console.error("Error parsing user from localStorage:", e);
    }

    // Try to get from token
    try {
      const token =
        localStorage.getItem("adminToken") ||
        localStorage.getItem("staffToken") ||
        localStorage.getItem("customerToken") ||
        localStorage.getItem("bankingPartnerToken") ||
        localStorage.getItem("affiliatePartnerToken") ||
        localStorage.getItem("creditManagerToken");

      if (token) {
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const payload = JSON.parse(window.atob(base64));
        return {
          _id: payload.userId || payload.id,
          name: payload.name || "User",
          userType: payload.userType || payload.role || "staff",
        };
      }
    } catch (e) {
      console.error("Error extracting user from token:", e);
    }

    // Return minimal user object if all else fails
    return { _id: "unknown", name: "User", userType: "staff" };
  };

  // Use communicationAPI like CommunicationCenter
  const fetchConversations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await communicationAPI.getConversations();
      if (response && response.conversations) {
        setConversations(response.conversations);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setError('Failed to fetch conversations');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch conversations on mount
  useEffect(() => {
    fetchConversations();
  }, []);

  // Handle typing indicator
  useEffect(() => {
    if (currentConversation) {
      try {
        setTyping(
          currentConversation.conversationId || currentConversation._id,
          isTyping
        );
      } catch (err) {
        console.error("Error setting typing indicator:", err);
      }
    }
  }, [isTyping, currentConversation, setTyping]);
  
  // Refresh conversations when messages change
  useEffect(() => {
    if (messages.length > 0) {
      fetchConversations();
    }
  }, [messages.length]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, messagesEndRef]);

  // Handle window resize for mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle message input changes
  const handleMessageChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    // Update typing indicator
    if (value.length > 0 && !isTyping) {
      setIsTyping(true);
    } else if (value.length === 0 && isTyping) {
      setIsTyping(false);
    }
  };
  
  // Handle conversation selection - replicate CommunicationCenter logic
  const handleSelectConversation = (conversation) => {
    selectConversation(conversation);
    if (isMobileView) {
      setShowChatOnMobile(true);
    }
  };
  
  // Use communicationAPI like CommunicationCenter
  const createConversation = async (participants, conversationType = "direct", title, description) => {
    try {
      setError(null);
      
      const response = await communicationAPI.createConversation(
        participants,
        conversationType,
        title,
        description
      );
      
      if (response && response.conversation) {
        // Add new conversation to list
        setConversations(prev => [response.conversation, ...prev]);
        return response;
      }
      throw new Error('Failed to create conversation');
    } catch (error) {
      console.error('Failed to create conversation:', error);
      setError('Failed to create conversation');
      throw error;
    }
  };
  
  // Clear error function
  const clearErrorLocal = () => {
    setError(null);
  };
  
  // Handle back button on mobile
  const handleBackToList = () => {
    setShowChatOnMobile(false);
  };

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !currentConversation) return;

    try {
      const currentUser = getCurrentUser();
      const userId = currentUser._id || currentUser.id;
      const userName = currentUser.name || "User";

      // Make sure we have a valid conversation ID
      const conversationId =
        currentConversation.conversationId || currentConversation._id;
      if (!conversationId) {
        console.error("No valid conversation ID found");
        return;
      }

      await sendMessage(conversationId, newMessage.trim());
      setNewMessage("");
      setIsTyping(false);
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  // Handle enter key
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Filter conversations based on search
  const filteredConversations = conversations.filter(
    (conv) =>
      conv.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.participants?.some((p) =>
        p.userId?.name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
  );

  // Get conversation title - show just the user name
  const getConversationTitle = (conversation) => {
    const currentUser = getCurrentUser();
    const userId = currentUser._id || currentUser.id;

    const otherParticipants = conversation.participants?.filter((p) => {
      const participantId = typeof p.userId === "string" ? p.userId : p.userId?._id;
      return participantId !== userId;
    }) || [];

    if (otherParticipants.length === 1) {
      return otherParticipants[0]?.userId?.name || "Unknown User";
    } else if (otherParticipants.length > 1) {
      return `${otherParticipants[0]?.userId?.name || "User"} +${
        otherParticipants.length - 1
      }`;
    }

    return "Direct Message";
  };

  // Get conversation preview
  const getConversationPreview = (conversation) => {
    if (conversation.lastMessage?.content) {
      let content;

      if (typeof conversation.lastMessage.content === "string") {
        content = conversation.lastMessage.content;
      } else if (conversation.lastMessage.content?.text) {
        content = conversation.lastMessage.content.text;
      } else if (
        typeof conversation.lastMessage.content === "object" &&
        conversation.lastMessage.content !== null
      ) {
        content = JSON.stringify(conversation.lastMessage.content);
      } else {
        content = "Message content unavailable";
      }

      return content.length > 50 ? content.substring(0, 50) + "..." : content;
    }
    return "No messages yet";
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (diffInHours < 48) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString();
    }
  };

  // Check if user is online
  const isUserOnline = (userId) => {
    return conversations.some((conv) =>
      conv.participants?.some(
        (p) =>
          p.userId?._id === userId &&
          p.lastSeen &&
          new Date() - new Date(p.lastSeen) < 5 * 60 * 1000
      )
    );
  };

  return (
    <div className="flex h-[80vh] bg-[#131313] rounded-lg m-4 mt-0.5 p-4 overflow-hidden">
      {/* Conversations Sidebar */}
      <div className={`${isMobileView && showChatOnMobile ? 'hidden' : isMobileView ? 'w-full p-2 ' : 'w-1/3'} border border-[#333333]  ${isMobileView ? 'rounded' : 'rounded-lg'} bg-[#131313] flex flex-col overflow-hidden`}>
        <div className="flex items-center justify-between p-4 border-b  border-[#333333] bg-[#131313]">
          <span className="text-lg font-semibold text-white">
            Conversations
          </span>
          <button
            className="bg-[#FFD600] text-black px-3 py-1 rounded flex items-center gap-1"
            onClick={() => setShowNewChat(true)}
          >
            New <span className="text-xl font-bold">+</span>
          </button>
        </div>

        {/* Conversations List */}
        <div className="overflow-y-auto h-full bg-[#131313]  flex-1">
          {loading ? (
            <div className="p-4 text-center text-gray-400">
              Loading conversations...
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="p-4 text-center bg text-gray-400">
              No conversations found
            </div>
          ) : (
            filteredConversations.map((conversation) => (
              <div
                key={
                  conversation.conversationId ||
                  conversation._id ||
                  `conv-${Math.random()}`
                }
                onClick={() => handleSelectConversation(conversation)}
                className={`p-4 border-b border-[#333333] cursor-pointer hover:bg-[#2a2a2a] transition-colors ${
                  currentConversation?.conversationId ===
                    conversation.conversationId ||
                  currentConversation?._id === conversation._id
                    ? "bg-[#2a2a2a] border-l-4 border-l-[#FFD600]"
                    : ""
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-medium text-white truncate">
                        {getConversationTitle(conversation)}
                      </h3>
                      {conversation.participants?.some((p) => {
                        const currentUser = getCurrentUser();
                        const userId = currentUser._id || currentUser.id;
                        return (
                          p.userId?._id !== userId &&
                          isUserOnline(p.userId?._id)
                        );
                      }) && (
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 truncate mt-1">
                      {getConversationPreview(conversation)}
                    </p>
                  </div>
                  <div className="text-xs text-gray-500 ml-2">
                    {formatTimestamp(
                      conversation.lastMessage?.timestamp || 
                      conversation.updatedAt || 
                      conversation.createdAt
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`${isMobileView && !showChatOnMobile ? 'hidden' : isMobileView ? 'w-full p-2' : 'flex-1'} flex flex-col bg-[#131313] rounded-lg border border-[#333333] ${!isMobileView ? 'ml-4' : ''}`}>
        {currentConversation ? (
          <>
            {/* Chat Header */}
            <div className={`${isMobileView ? 'p-3' : 'p-4'} border-b border-[#333333] flex items-center justify-between`}>
              {isMobileView && (
                <button 
                  onClick={handleBackToList}
                  className="mr-2 text-gray-400 hover:text-white flex-shrink-0"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <div className={`flex items-center ${isMobileView ? 'space-x-2' : 'space-x-3'} flex-1 min-w-0`}>
                <div className={`${isMobileView ? 'w-8 h-8' : 'w-10 h-10'} bg-[#FFD600] rounded-full flex items-center justify-center flex-shrink-0`}>
                  <span className={`text-black font-medium ${isMobileView ? 'text-sm' : ''}`}>
                    {getConversationTitle(currentConversation)
                      .charAt(0)
                      .toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className={`font-medium text-white truncate ${isMobileView ? 'text-sm' : ''}`}>
                    {getConversationTitle(currentConversation)}
                  </h2>
                  <p className={`text-gray-400 ${isMobileView ? 'text-xs' : 'text-sm'}`}>
                    {currentConversation.participants?.length || 0} participants
                  </p>
                </div>
              </div>
            </div>

            {/* Messages Area */}
            <div className={`flex-1 overflow-y-auto ${isMobileView ? 'p-2' : 'p-4'} space-y-4 bg-[#131313]`}>
              {messages.length === 0 ? (
                <div className="text-center text-gray-400 mt-8">
                  <ChatAlt2Icon className="h-12 w-12 mx-auto mb-4 text-gray-600" />
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                messages.map((message, index) => {
                  const currentUser = getCurrentUser();
                  const userId = currentUser._id || currentUser.id;

                  const isOwnMessage =
                    message.sender?.userId === userId ||
                    message.sender?.userId?._id === userId ||
                    message.senderId === userId ||
                    message.isMine ||
                    (message.sender && typeof message.sender === 'string' && message.sender === userId);

                  // Get message content - handle both communication API and chat API formats
                  let messageContent = "";
                  
                  if (typeof message.content === "string") {
                    messageContent = message.content;
                  } else if (message.content?.text) {
                    messageContent = message.content.text;
                  } else if (message.text) {
                    messageContent = message.text;
                  } else if (message.message) {
                    messageContent = message.message;
                  } else if (typeof message.content === "object" && message.content !== null) {
                    // Try to extract text from object
                    if (message.content.message) {
                      messageContent = message.content.message;
                    }
                  }
                  
                  // Skip rendering if no valid content
                  if (!messageContent || messageContent.trim() === "") {
                    return null;
                  }

                  return (
                    <div
                      key={message._id || `msg-${index}`}
                      className={`flex ${
                        isOwnMessage ? "justify-end" : "justify-start"
                      }`}
                    >
                      <div
                        className={`${isMobileView ? 'max-w-[85%]' : 'max-w-xs lg:max-w-md'} ${isMobileView ? 'px-3 py-2' : 'px-4 py-2'} rounded-lg ${
                          isOwnMessage
                            ? "bg-[#FFD600] text-black"
                            : "bg-[#2a2a2a] text-white"
                        }`}
                      >
                        {!isOwnMessage && (
                          <div className={`text-gray-400 mb-1 ${isMobileView ? 'text-xs' : 'text-xs'}`}>
                            {message.sender?.name || "Unknown User"}
                          </div>
                        )}
                        <div className={`${isMobileView ? 'text-sm' : 'text-sm'} break-words`}>{messageContent}</div>
                        <div className="flex items-center justify-between mt-1">
                          <span className={`${isOwnMessage ? 'text-gray-800' : 'text-gray-300'} items-end ${isMobileView ? 'text-xs' : 'text-[8px]'}`}>
                            {formatTimestamp(
                              message.createdAt || message.sentAt
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}

              {/* Typing Indicators */}
              {Array.from(typingUsers.values()).map((user) => (
                <div key={user.name} className="flex justify-start">
                  <div className="bg-[#2a2a2a] px-4 py-2 rounded-lg">
                    <div className="text-xs text-gray-400 mb-1">
                      {user.name}
                    </div>
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.1s" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: "0.2s" }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}

              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="p-4 bg-transparent">
              <div className="flex items-end space-x-2">
                <div className="flex-1 relative">
                  <textarea
                    ref={messageInputRef}
                    value={newMessage}
                    onChange={handleMessageChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Type a message..."
                    className="w-full px-4 py-3 bg-[#2a2a2a] border border-[#333333] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 resize-none"
                    rows="1"
                    style={{ minHeight: "44px", maxHeight: "120px" }}
                  />
                  <div className="absolute right-2 bottom-2 flex items-center space-x-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        console.log("File selected:", e.target.files[0]);
                      }}
                    />
                  </div>
                </div>
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || !currentConversation}
                  className="bg-[#FFD600] hover:bg-[#FFD600]/80 disabled:bg-gray-600 disabled:cursor-not-allowed text-black p-3 rounded-lg transition-colors mb-2.5 px-3.5"
                >
                  <PaperAirplaneIcon className="h-5 w-5 rotate-90" />
                </button>
              </div>
            </div>
          </>
        ) : (
          /* Welcome Screen */
          <div className="hidden lg:flex flex-1 flex-col items-center justify-center p-4">
            <div className="text-center">
              <ChatAlt2Icon className="h-16 w-16 text-[#FFD600] mx-auto mb-4" />
              <h2 className="text-xl font-medium text-white mb-2">
                Welcome to Team Messaging
              </h2>
              <p className="text-gray-400 mb-6 max-w-md">
                Start a conversation with your team members or select an
                existing chat to continue.
              </p>
              <button
                onClick={() => setShowNewChat(true)}
                className="bg-[#FFD600] hover:bg-[#FFD600]/80 text-black px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Start New Conversation
              </button>
            </div>
          </div>
        )}
      </div>

      {/* New Chat Modal */}
      {showNewChat && (
        <NewChatModal
          onClose={() => setShowNewChat(false)}
          createConversation={createConversation}
          setActiveConversation={(conversation) => {
            selectConversation(conversation);
            setShowNewChat(false);
            if (isMobileView) {
              setShowChatOnMobile(true);
            }
          }}
        />
      )}

      {/* Error Display */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg z-50">
          <div className="flex items-center space-x-2">
            <span>{error}</span>
            <button
              onClick={clearErrorLocal}
              className="text-white hover:text-gray-200"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamMessaging;
